import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';

/**
 * The Media Library.
 *
 * Backs both the full-page browser at `/admin/media` and the Library tab of
 * `<AssetPicker>`, so the two can never disagree about what exists.
 */

export type AssetSource = 'upload' | 'ai' | 'stock' | 'brand_kit' | 'import' | 'unknown';

export interface AssetUsageRef {
  entity_type: string;
  entity_id: string | null;
  label: string | null;
  admin_path: string | null;
}

export interface MediaAsset {
  id: string;
  key: string;
  url: string | null;
  mime: string | null;
  bytes: number | null;
  width: number | null;
  height: number | null;
  alt_text: string | null;
  title: string | null;
  source: AssetSource;
  /** Shape depends on `source`: prompt/model/seed for AI, attribution for stock. */
  source_meta: Record<string, unknown>;
  created_at: string;
  used_on: AssetUsageRef[];
  usage_count: number;
}

export interface MediaFilters {
  q?: string;
  source?: AssetSource | '';
  folder?: string;
  unused?: boolean;
  page?: number;
  limit?: number;
}

export interface MediaListResult {
  assets: MediaAsset[];
  total: number;
  page: number;
  limit: number;
  /** The "unused" filter scans a bounded window; true means there may be more. */
  truncated: boolean;
}

export const MEDIA_KEY = ['media'] as const;

function toQuery(f: MediaFilters): string {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.source) p.set('source', f.source);
  if (f.folder) p.set('folder', f.folder);
  if (f.unused) p.set('unused', '1');
  if (f.page && f.page > 1) p.set('page', String(f.page));
  if (f.limit) p.set('limit', String(f.limit));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function useMediaAssets(filters: MediaFilters = {}, enabled = true) {
  return useQuery({
    queryKey: [...MEDIA_KEY, 'list', filters],
    enabled,
    // A grid of thumbnails re-fetching on every focus change is visible churn
    // for content that changes when the person on this screen changes it.
    staleTime: 30_000,
    queryFn: () => api.get<MediaListResult>(`/admin/media${toQuery(filters)}`),
  });
}

export function useMediaAsset(id: string | null) {
  return useQuery({
    queryKey: [...MEDIA_KEY, 'detail', id],
    enabled: !!id,
    queryFn: () => api.get<MediaAsset>(`/admin/media/${id}`),
  });
}

export interface MediaFacets {
  folders: { folder: string; count: number; bytes: number }[];
  sources: { source: AssetSource; n: number }[];
}

export function useMediaFacets(enabled = true) {
  return useQuery({
    queryKey: [...MEDIA_KEY, 'facets'],
    enabled,
    staleTime: 60_000,
    queryFn: () => api.get<MediaFacets>('/admin/media/folders'),
  });
}

export function useUpdateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; alt_text?: string | null; title?: string | null }) =>
      api.patch<MediaAsset>(`/admin/media/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEDIA_KEY });
    },
  });
}

/**
 * Delete an asset.
 *
 * `force` is the deliberate override for a file that is still referenced. The
 * server refuses without it (409, with the reference list) and requires publish
 * permission with it — deleting an in-use image changes the live site.
 */
export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      api.delete<{ success: boolean; key: string; was_in_use: number }>(
        `/admin/media/${id}${force ? '?force=1' : ''}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEDIA_KEY });
    },
  });
}

/** Index whatever is in the bucket but not in the index. */
export function useReconcileMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prune?: boolean) =>
      api.post<{ scanned: number; indexed: number; missing: number; truncated: boolean }>(
        `/admin/media/reconcile${prune ? '?prune=1' : ''}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEDIA_KEY });
    },
  });
}

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const SOURCE_LABELS: Record<AssetSource, string> = {
  upload: 'Uploaded',
  ai: 'AI generated',
  stock: 'Stock photo',
  brand_kit: 'Brand kit',
  import: 'Imported',
  unknown: 'Found in storage',
};
